/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useEffect, useMemo} from 'react';
import {useInfiniteQuery, useQueryClient} from 'react-query';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadGroups} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {GenericObject} from '@ion/core/src/types/entities';
import {GroupSort} from '@ion/core/src/types/services';
import {
  createKey,
  QueryKey
} from '../config/QueryKeys';
import {
  createQueryResult,
  DurationResult,
  getStaleTime
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export interface UseInfiniteGroupsQueryProps {
  accountId: number;
  searchString: string;
  includeSubGroups?: boolean;
  staleTime?: number | DurationResult;
  sortOn?: GroupSort[];
  maxResults?: number;
  enabled?: boolean;
}

export interface UseInfiniteGroupsQueryResult {
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  data: GroupResult[];
}

export type GroupResult = {
  name: string;
  id: number;
  account?: GenericObject;
  accountId: number | null;
};

interface LoadGroupsResult {
  totalCount: number;
  groups: GroupResult[];
}

/**
 * The `useInfiniteGroupsQuery` hook provides a paginated query for retrieving
 * `Groups` for use within an infinite scroll UI.
 *
 * ```typescript
 *   import {useInfiniteGroupsQuery} from '@ion/query/src/hooks/useInfiniteGroupsQuery';
 *
 *   ...
 *
 *   const {
 *     isFetchingNextPage,
 *     fetchNextPage,
 *     hasNextPage,
 *     data,
 *   } = useInfiniteGroupsQuery({
 *     accountId    : 212900,
 *     searchString : 'Test Group 3',
 *     enabled      : searchString.length > 2
 *   });
 *
 * ```
 *
 * @param accountId    The account ID to search for groups
 * @param searchString The search string to filter the groups
 * @param staleTime    The time in milliseconds to keep the query data in cache
 * @param sortOn       The sort order for the results
 * @param maxResults   The number of results to return per page
 * @param enabled      When true, the query will be executed immediately.
 *
 * @returns a list of groups for the Devices Grid Groups Search Dropdown.
 *
 */
export const useInfiniteGroupsQuery = ({
  searchString,
  accountId,
  sortOn = ['+name'],
  includeSubGroups = false,
  maxResults = 20,
  staleTime = getStaleTime(0, false, false),
  enabled = true
}: UseInfiniteGroupsQueryProps) => {
  const queryClient = useQueryClient();

  const key = useMemo(() => (createKey(
    QueryKey.GROUPS_SEARCH,
    accountId,
    searchString
  )), [accountId, searchString]);

  const fetchGroupResults = async({pageParam = 1}): Promise<LoadGroupsResult> => {
    try {
      const reponse = await loadGroups({
        accountId : Number(accountId),
        name      : searchString,
        pageSize  : maxResults,
        pageId    : pageParam,
        includeSubGroups,
        maxResults,
        sortOn
      }) as LoadGroupsResult;
      return reponse;
    } catch (err) {
      error(err);
      return {totalCount: 0, groups: []};
    }
  };

  const getNextPageParam = (lastPage, allPages) => {
    const allFetchedItems = allPages?.reduce((total, page) => total + page?.groups?.length, 0);
    if (allFetchedItems >= lastPage?.totalCount) {
      return undefined;
    }
    return allPages?.length + 1;
  };

  const query = useInfiniteQuery<LoadGroupsResult>(key, fetchGroupResults, {
    staleTime : staleTime as number,
    getNextPageParam,
    enabled
  }
  );

  const transformedData = query.data?.pages?.flatMap(page => (
    page?.groups?.map((group: GroupResult) => ({
      accountId : entityId(group?.account) || null,
      name      : group?.name,
      id        : group?.id
    })
    ))) || [];

  useEffect(() => {
    queryClient.invalidateQueries([QueryKey.GROUPS_SEARCH, accountId, searchString]);
  }, [accountId, includeSubGroups]);

  return createQueryResult(query, {
    isFetchingNextPage : query.isFetchingNextPage,
    fetchNextPage      : query.fetchNextPage,
    hasNextPage        : query.hasNextPage,
    isLoading          : query.isLoading,
    data               : transformedData
  } as Partial<UseDefaultQueryResult> & UseInfiniteGroupsQueryResult);
};

export default useInfiniteGroupsQuery;
